import http from '@/utils/httpClient';
import { toaster } from '@/utils/toast';
import { Action, Thunk, action, thunk } from 'easy-peasy';

export interface workExperience {
  id: string;
  providerId: string;
  jobTitle: string;
  startDate: string;
  endDate: string;
  company: string;
  workType: string;
  jobType: string;
  description: string;
}

export interface workExpPayload {
  company: string;
  start_date: string;
  end_date: string | null;
  description: string;
  work_type: string;
  job_title: string;
  job_type: string;
  id: string;
}

export interface education {
  id: string;
  providerId: string;
  degree: string;
  startDate: string;
  endDate: string;
  school: string;
}

export interface educationPayload {
  degree: string;
  start_date: string;
  end_date: string;
  school: string;
  id: string;
}

export interface profilePic {
  data: [];
  type: string;
}

export interface profileDescription {
  userId: string;
  name: string;
  emailId: string;
  type: string;
  status: string;
  providerId: string;
  linkedInUrl: string;
  phone: string;
  brokerValue: true;
  brokerName: string;
  experience: string;
  skills: string[];
  about: string;
  cv: string;
  role: string;
  profilePic: profilePic;
  location: string;
  workExperiences: workExperience[];
  education: education[];
  tosAcceptedAt: string | null;
}

export interface jobSearchPayload {
  search: string;
  jobType: string[];
  workType: string;
  experience: string[];
  jobSkills: string[];
  minBudget: number | null;
  maxBudget: number | null;
}

export interface RequestStatus {
  id: string;
  status: string;
}

export interface profilePic {
  data: [];
  type: string;
}
export interface AdvisorProfile {
  id: string;
  company: string;
  about: string;
  duties: string;
  phone: string;
  firmAffiliation: string;
  crmName: string;
  location: string;
  linkedInUrl: string;
  profilePic: profilePic;
  jobPosts: JobPost[];
  role: string;
  crdNumber: string;
}
export interface JobPost {
  id: string;
  jobTitle: string;
  serviceType: string;
  budget: number;
  description: string;
  applicants: number;
  jobType: string;
  workType: string;
}

export interface advisorProfileDescription {
  id: string;
  firstName: string;
  lastName: string;
  emailId: string;
  requestStatus: RequestStatus;
  advisor: AdvisorProfile;
}
export interface ExpAndEduDeletePayload {
  id: string;
  type: string;
}

export interface IProviderModel {
  profileDescription: profileDescription | undefined;
  jobSearchPayload: jobSearchPayload;
  advisorProfileDescription: advisorProfileDescription | undefined;

  /*------------ ACTION TYPE -------------*/
  setProfileDescription: Action<IProviderModel, profileDescription>;
  setJobSearchPayload: Action<IProviderModel, jobSearchPayload>;
  setAdvisorProfileDescription: Action<IProviderModel, advisorProfileDescription>;

  /*------------ THUNK TYPE -------------*/
  getProfileDescription: Thunk<IProviderModel>;
  getAdvisorProfileDescription: Thunk<IProviderModel, string>;
  addWorkExperience: Thunk<IProviderModel, workExpPayload>;
  addEducation: Thunk<IProviderModel, educationPayload>;
  editProfile: Thunk<IProviderModel, any>;
  editWorkExp: Thunk<IProviderModel, workExpPayload>;
  editEducation: Thunk<IProviderModel, educationPayload>;
  deleteEduAndExp: Thunk<IProviderModel, ExpAndEduDeletePayload>;
}

const ProviderFuntionalModel: IProviderModel = {
  profileDescription: undefined,
  advisorProfileDescription: undefined,
  jobSearchPayload: {
    search: '',
    jobType: [],
    workType: '',
    experience: [],
    maxBudget: 1000000000000,
    minBudget: 0,
    jobSkills: [],
  },

  /*------------ ACTION TYPE -------------*/
  setProfileDescription: action((state, value) => {
    state.profileDescription = value;
  }),

  setJobSearchPayload: action((state, val) => {
    state.jobSearchPayload = val;
  }),

  setAdvisorProfileDescription: action((state, value) => {
    state.advisorProfileDescription = value;
  }),

  /*------------ THUNK TYPE -------------*/
  getProfileDescription: thunk(async (action) => {
    await http()
      .get('/provider/profile')
      .then((res) => {
        action.setProfileDescription(res.data);
      })
      .catch(() => toaster('error', 'Error in fetching profile info'));
  }),

  addWorkExperience: thunk(async (_action, payload) => {
    const response = await http()
      .post('/provider/work', payload)
      .then(() => {
        toaster('success', 'Added new work experience');
        return new Promise((resolve) => resolve('added'));
      })
      .catch(() => {
        toaster('error', 'Error in adding work experience');
        return new Promise((resolve) => resolve('error'));
      });
    return response;
  }),

  addEducation: thunk(async (_action, payload) => {
    const response = await http()
      .post('/provider/education', payload)
      .then(() => {
        toaster('success', 'Added new education');
        return new Promise((resolve) => resolve('added'));
      })
      .catch(() => {
        toaster('error', 'Error in adding education');
        return new Promise((resolve) => resolve('error'));
      });
    return response;
  }),

  editProfile: thunk(async (_action, payload) => {
    const response = await http({ 'Content-Type': 'multipart/form-data' })
      .put('/provider/profile', payload)
      .then(() => {
        toaster('success', 'Profile updated successfully');
        return new Promise((resolve) => resolve('success'));
      })
      .catch(() => {
        toaster('error', 'Error in editing profile');
        return new Promise((resolve) => resolve('error'));
      });
  }),

  getAdvisorProfileDescription: thunk(async (action, payload) => {
    const response = await http()
      .get(`/advisor/profile/${payload}`)
      .then((res) => {
        action.setAdvisorProfileDescription(res.data);
        return new Promise((resolve) => resolve(true));
      })
      .catch(() => {
        toaster('error', 'Error in fetching advisor details');
        return new Promise((resolve) => resolve(false));
      });
    return response;
  }),
  editWorkExp: thunk(async (_action, payload) => {
    await http()
      .put(`/provider/work/${payload.id}`, payload)
      .then(() => {
        toaster('success', 'Work experience updated successfully');
      })
      .catch(() => {
        toaster('error', 'Error in editing experience');
      });
  }),
  editEducation: thunk(async (_action, payload) => {
    await http()
      .put(`/provider/education/${payload.id}`, payload)
      .then(() => {
        toaster('success', 'Education updated successfully');
      })
      .catch(() => {
        toaster('error', 'Error in editing education');
      });
  }),

  deleteEduAndExp: thunk(async (action, payload) => {
    const response = await http()
      .delete(`provider/delete?id=${payload.id}&type=${payload.type}`)
      .then((res) => {
        toaster('success', `${payload.type} deleted successfully`);
        return new Promise((resolve) => resolve('deleted'));
      })
      .catch(() => {
        toaster('error', `Error in deleteing ${payload.type}`);
        return new Promise((resolve) => resolve('error'));
      });
    return response;
  }),
};

export default ProviderFuntionalModel;
